import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { readApiErrorResponse } from "@/lib/api-error";
import { platformFetch } from "@/services/http/platform-fetch";

type EmailUsageCount = { queued: number; signInCodes: number; total: number };

export type SchoolEmailUsage = {
  tenantId: string;
  slug: string;
  name: string;
  today: EmailUsageCount & { held: number };
  month: EmailUsageCount;
  /** sh-fix F3: "reserve" — only admins get sign-in codes; "limit" — the cap is reached. */
  state: "normal" | "reserve" | "limit";
  warning: { daily: boolean; monthly: boolean };
};

/** MT-1 INFRA-024 S6 (FR-47): one row per school — each sends through its own Resend account. */
export type EmailUsage = {
  schools: SchoolEmailUsage[];
  thresholds: { daily: number; monthly: number };
  /** sh-fix F3: each school account's daily cap and the admins' share of it. */
  limits: { daily: number; adminReserve: number };
};

async function fetchEmailUsage(): Promise<EmailUsage> {
  const res = await platformFetch("/platform/admin/email-usage");
  if (!res.ok) throw await readApiErrorResponse(res);
  return ((await res.json()) as { data: EmailUsage }).data;
}

/**
 * H1b / MT-1 INFRA-024 S6: emails each school sent today and this month (sign-in codes
 * included), with mail held until midnight UTC, so the owner sees a school's Resend plan limit
 * coming (free plan: 100 a day, 3,000 a month; warning at 80 / 2,400 by default) and whether
 * its admin reserve or cap is reached.
 */
export function EmailUsageCard() {
  const t = useTranslations("platform.emailUsage");
  const query = useQuery({ queryKey: ["platform-email-usage"], queryFn: fetchEmailUsage });
  const data = query.data;

  return (
    <section
      className="mt-8 rounded-lg border border-[var(--border-strong)] p-4"
      data-testid="email-usage"
      aria-labelledby="email-usage-title"
    >
      <h2 id="email-usage-title" className="text-lg font-semibold">
        {t("title")}
      </h2>
      {query.isError && <p className="mt-2 text-sm text-[var(--danger)]">{t("loadFailed")}</p>}
      {query.isPending && <p className="mt-2 text-sm text-[var(--text-muted)]">{t("loading")}</p>}
      {data && (
        <>
          {data.schools.map((school) =>
            school.state !== "normal" ? (
              <p
                key={`state-${school.tenantId}`}
                role="alert"
                data-testid={`email-usage-state-${school.slug}`}
                className="mt-3 rounded-md bg-[var(--surface-3)] p-3 text-sm font-medium text-[var(--danger)]"
              >
                {school.name}:{" "}
                {t(school.state === "limit" ? "stateLimit" : "stateReserve", {
                  sent: school.today.total,
                  limit: data.limits.daily,
                })}
              </p>
            ) : null,
          )}
          {data.schools.map((school) =>
            school.warning.daily || school.warning.monthly ? (
              <p
                key={`warning-${school.tenantId}`}
                role="alert"
                data-testid={`email-usage-warning-${school.slug}`}
                className="mt-3 rounded-md bg-[var(--surface-3)] p-3 text-sm font-medium text-[var(--danger)]"
              >
                {school.name}:{" "}
                {[
                  school.warning.daily && t("warningDaily", { threshold: data.thresholds.daily }),
                  school.warning.monthly &&
                    t("warningMonthly", { threshold: data.thresholds.monthly }),
                ]
                  .filter(Boolean)
                  .join(" ")}
              </p>
            ) : null,
          )}
          {data.schools.length === 0 ? (
            <p className="mt-3 text-sm text-[var(--text-muted)]" data-testid="email-usage-empty">
              {t("empty")}
            </p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-[var(--text-muted)]">
                  <tr>
                    <th scope="col" className="py-1 pr-3 font-medium">
                      {t("school")}
                    </th>
                    <th scope="col" className="py-1 pr-3 font-medium">
                      {t("today")}
                    </th>
                    <th scope="col" className="py-1 pr-3 font-medium">
                      {t("month")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.schools.map((school) => (
                    <tr
                      key={school.tenantId}
                      data-testid={`email-usage-row-${school.slug}`}
                      className="border-t border-[var(--border)]"
                    >
                      <th scope="row" className="py-2 pr-3 font-medium">
                        {school.name}
                      </th>
                      <td className="py-2 pr-3" data-testid={`email-usage-today-${school.slug}`}>
                        <span className="font-semibold">{school.today.total}</span>{" "}
                        <span className="text-xs text-[var(--text-muted)]">
                          {t("breakdown", { codes: school.today.signInCodes })}
                          {school.today.held > 0
                            ? ` · ${t("held", { held: school.today.held })}`
                            : ""}
                        </span>
                      </td>
                      <td className="py-2 pr-3" data-testid={`email-usage-month-${school.slug}`}>
                        <span className="font-semibold">{school.month.total}</span>{" "}
                        <span className="text-xs text-[var(--text-muted)]">
                          {t("breakdown", { codes: school.month.signInCodes })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-3 text-xs text-[var(--text-muted)]">
            {t("warnAt", { threshold: data.thresholds.daily })} ·{" "}
            {t("warnAt", { threshold: data.thresholds.monthly })} ·{" "}
            {t("dailyLimit", { limit: data.limits.daily, reserve: data.limits.adminReserve })}
          </p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">{t("note")}</p>
        </>
      )}
    </section>
  );
}
